import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useOrg } from 'src/shared/contexts/OrgContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { ExtractionModel } from 'src/shared/types/extraction-model.types';

type NewExtractionModelProps = {
  modelId: string;
  name: string;
  description?: string;
};

type UpdateExtractionModelProps = {
  modelId: string;
  name?: string;
  description?: string;
  status?: 'ACTIVE' | 'INACTIVE';
  includeAttachments?: boolean;
};

type GenerateFromPdfsProps = {
  name: string;
  description?: string;
  pdfs: File[];
};

export function useMutateExtractionModel() {
  const queryClient = useQueryClient();
  const { orgFetch, currentOrg, role } = useOrg();
  const { errorToast } = useToast();

  const queryKey = ['next-gen', 'models', 'extraction', genQueryKey(currentOrg.id)];
  const singleModelQueryKey = (modelId: string) => ['next-gen', 'models', modelId, genQueryKey(currentOrg.id)];

  const newExtractionModel = useMutation({
    mutationFn: async ({ modelId, name, description }: NewExtractionModelProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot create models');
      }
      const res = await orgFetch<ExtractionModel>(`/api/v2/next-gen-models/extraction`, {
        method: 'POST',
        body: JSON.stringify({
          name: name || modelId,
          description: description || '',
          entities: [],
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error('Unable to create extraction model');
      }
      return data;
    },
    onSuccess: (data: ExtractionModel) => {
      // Invalidate the list query to refetch
      queryClient.invalidateQueries({ queryKey });
      // Set the single model in cache
      queryClient.setQueryData(singleModelQueryKey(data.id), data);
      mixpanel.track('Next-Gen Extraction Model Created');
    },
  });

  const updateExtractionModel = useMutation({
    mutationFn: async ({ modelId, ...data }: UpdateExtractionModelProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot update models');
      }
      const res = await orgFetch<ExtractionModel>(`/api/v2/next-gen-models/extraction/${modelId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error('Extraction model update failed');
      }
      return responseData;
    },
    onMutate: async ({ modelId, ...data }: UpdateExtractionModelProps) => {
      await queryClient.cancelQueries({ queryKey: singleModelQueryKey(modelId) });
      const previousData = queryClient.getQueryData<ExtractionModel>(singleModelQueryKey(modelId));

      // Optimistically update the cache
      if (previousData) {
        queryClient.setQueryData<ExtractionModel>(singleModelQueryKey(modelId), {
          ...previousData,
          ...data,
        });
      }

      return { previousData };
    },
    onSuccess: (data: ExtractionModel) => {
      // Update both single model and list caches
      queryClient.setQueryData(singleModelQueryKey(data.id), data);
      queryClient.invalidateQueries({ queryKey });
      mixpanel.track('Next-Gen Extraction Model Updated');
    },
    onError: (_, __, context) => {
      errorToast('Failed to update extraction model');
      // Revert optimistic update
      if (context?.previousData) {
        queryClient.setQueryData(
          singleModelQueryKey((context.previousData as ExtractionModel).id),
          context.previousData
        );
      }
    },
  });

  const deleteExtractionModel = useMutation({
    mutationFn: async (modelId: string) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot delete models');
      }
      const res = await orgFetch<{ message: string }>(`/api/v2/next-gen-models/extraction/${modelId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error('Unable to delete extraction model');
      }
      return modelId;
    },
    onSuccess: (modelId: string) => {
      // Remove from single model cache
      queryClient.removeQueries({ queryKey: singleModelQueryKey(modelId) });
      // Invalidate list to refetch
      queryClient.invalidateQueries({ queryKey });
      mixpanel.track('Next-Gen Extraction Model Deleted');
    },
    onError: () => {
      errorToast('Failed to delete extraction model');
    },
  });

  const generateFromPdfs = useMutation({
    mutationFn: async ({ name, description, pdfs }: GenerateFromPdfsProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot create models');
      }
      if (pdfs.length === 0) {
        throw new Error('At least one PDF file is required');
      }
      if (pdfs.length > 5) {
        throw new Error('Maximum 5 PDF files allowed');
      }

      // Create FormData
      const formData = new FormData();

      // Add PDF files
      for (const pdf of pdfs) {
        formData.append('pdfs', pdf);
      }

      // Add form fields
      formData.append('name', name);
      if (description) {
        formData.append('description', description);
      }

      const res = await orgFetch<ExtractionModel>(
        `/api/v2/next-gen-models/extraction/generate-from-pdfs`,
        {
          method: 'POST',
          body: formData,
          // Don't set Content-Type header - browser will set it with boundary
        },
        true // skipDefaultHeaders - don't add Content-Type: application/json for FormData
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error('Unable to generate extraction model from PDFs');
      }
      return data;
    },
    onSuccess: (data: ExtractionModel) => {
      // Invalidate the list query to refetch
      queryClient.invalidateQueries({ queryKey });
      // Set the single model in cache
      queryClient.setQueryData(singleModelQueryKey(data.id), data);
      mixpanel.track('Next-Gen Extraction Model Generated From PDFs');
    },
  });

  return {
    newExtractionModel,
    updateExtractionModel,
    deleteExtractionModel,
    generateFromPdfs,
  };
}
